'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { useNavHighlight } from '@paeffgen/react/crm-documents/ui';
import {
  MATERIAL_REQUESTS_QUERY,
  CREATE_MATERIAL_REQUEST_MUTATION,
  UPDATE_MATERIAL_REQUEST_MUTATION,
  SUBMIT_MATERIAL_REQUEST_MUTATION,
  DELETE_MATERIAL_REQUEST_MUTATION,
  type MaterialRequest,
  type MaterialRequestConnection,
  type MaterialRequestItem,
  type MaterialRequestItemInput,
  MaterialRequestStatus,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { getRequestAnzahl } from '@paeffgen/react/crm-documents/util';

// The GraphQL `MaterialRequestItemInput.quantity` is the **Anzahl** (pack
// count). The resolver stores `quantity` as **total quantity in base unit**.
// Whenever we replay existing items into an update mutation, we must convert
// their persisted total back to Anzahl so the backend doesn't multiply twice.
const toInputAnzahl = (
  i: Pick<MaterialRequestItem, 'quantity' | 'quantityPerPackage' | 'piecesPerPackage'>,
): number => getRequestAnzahl(i);

const DRAFT_QUERY_VARIABLES = {
  status: MaterialRequestStatus.DRAFT,
  first: 1,
};

/**
 * Hook replacing the in-memory CartContext from the prototype.
 * Persists the cart as a DRAFT MaterialRequest in the backend.
 */
export function useDraftRequest() {
  const { adminQueryVars, adminMutationVars, allCustomers, selectedCustomers } = useAdmin();
  const { markHighlight } = useNavHighlight();

  // Mutations require exactly one customer context. When "Alle Kunden" is on
  // without a single debitor selected, adminMutationVars is empty and writes
  // would silently go to the admin's own account — block this.
  const canMutate = !allCustomers || selectedCustomers.length === 1;

  const draftRefetch = useMemo(
    () => [
      { query: MATERIAL_REQUESTS_QUERY, variables: { ...DRAFT_QUERY_VARIABLES, ...adminQueryVars } },
    ],
    [adminQueryVars]
  );

  // 1. Query current DRAFT material request
  const { data, loading, refetch } = useQuery<{ materialRequests: MaterialRequestConnection }>(MATERIAL_REQUESTS_QUERY, {
    variables: { ...DRAFT_QUERY_VARIABLES, ...adminQueryVars },
    fetchPolicy: 'cache-and-network',
  });

  const currentDraft: MaterialRequest | null =
    data?.materialRequests?.edges?.[0]?.node ?? null;

  // 1b. NAV-Erfolgs-Banner: only shown as a confirmation right after the user
  // submits in this session. Filled from the submit mutation result (the NAV
  // Quote number is assigned synchronously by the resolver), auto-hidden after
  // a few seconds, and gone on reload. Deliberately NOT derived from the latest
  // SUBMITTED request — that never leaves SUBMITTED and would otherwise show the
  // banner permanently for every debitor (incl. cross-login admins).
  const [justSubmittedNav, setJustSubmittedNav] = useState<{
    navDocumentNo: string;
    navImportedAt: string | null;
  } | null>(null);

  const dismissNavBanner = useCallback(() => setJustSubmittedNav(null), []);

  useEffect(() => {
    if (!justSubmittedNav) return;
    const timer = setTimeout(() => setJustSubmittedNav(null), 8000);
    return () => clearTimeout(timer);
  }, [justSubmittedNav]);

  // 2. Mutations
  const [createRequest] = useMutation(CREATE_MATERIAL_REQUEST_MUTATION);
  const [updateRequest] = useMutation(UPDATE_MATERIAL_REQUEST_MUTATION);
  const [submitRequest] = useMutation<{ submitMaterialRequest: MaterialRequest }>(
    SUBMIT_MATERIAL_REQUEST_MUTATION,
  );
  const [deleteRequest] = useMutation(DELETE_MATERIAL_REQUEST_MUTATION);

  // 3. Cart UI state
  const [isOpen, setIsOpen] = useState(false);
  const hasOpenedOnce = useRef(false);

  const items: MaterialRequestItem[] = currentDraft?.items ?? [];
  const itemCount = items.length;
  const netTotal = currentDraft?.netTotal ?? 0;

  /**
   * Check if an article is already on the material list.
   * Returns the existing item or null.
   */
  const findExistingItem = useCallback(
    (articleNumber: string): MaterialRequestItem | null => {
      return items.find((i) => i.articleNumber === articleNumber) ?? null;
    },
    [items]
  );

  // 4. Actions

  const addItem = useCallback(
    async (articleNumber: string, quantity: number, note?: string) => {
      if (!canMutate) {
        toast.error('Bitte genau einen Debitor auswählen, um Artikel hinzuzufügen.');
        return;
      }
      try {
        if (!currentDraft) {
          await createRequest({
            variables: {
              items: [{ articleNumber, quantity, note }],
              ...adminMutationVars,
            },
            refetchQueries: [...draftRefetch],
          });
          toast.success(`${articleNumber} zur Materialanfrage hinzugefügt`);
        } else {
          const existingItems: MaterialRequestItemInput[] = currentDraft.items.map(
            (i) => ({
              articleNumber: i.articleNumber,
              quantity: toInputAnzahl(i),
              note: i.note ?? undefined,
            })
          );
          const existingIndex = existingItems.findIndex(
            (i) => i.articleNumber === articleNumber
          );
          const isDuplicate = existingIndex >= 0;
          if (isDuplicate) {
            existingItems[existingIndex].quantity += quantity;
          } else {
            existingItems.push({ articleNumber, quantity, note });
          }
          await updateRequest({
            variables: { id: currentDraft.id, items: existingItems, ...adminMutationVars },
            refetchQueries: [...draftRefetch],
          });
          if (isDuplicate) {
            toast.success(
              `${articleNumber} — Menge auf ${existingItems[existingIndex].quantity} erhöht`
            );
          } else {
            toast.success(`${articleNumber} zur Materialanfrage hinzugefügt`);
          }
        }

        markHighlight('materialanfrage');

        // Open sidebar only on the first add in this session
        if (!hasOpenedOnce.current) {
          setIsOpen(true);
          hasOpenedOnce.current = true;
        }
      } catch {
        toast.error('Fehler beim Hinzufügen. Bitte erneut versuchen.');
      }
    },
    [canMutate, currentDraft, createRequest, updateRequest, adminMutationVars, draftRefetch, markHighlight]
  );

  /**
   * Bulk-Add: adds multiple items in a single mutation. Avoids the
   * race condition that occurs when calling addItem() in a loop —
   * `currentDraft` is a stale closure value within the iteration.
   * Merges duplicates (same articleNumber) by summing quantities.
   */
  const addItems = useCallback(
    async (
      newItems: Array<{ articleNumber: string; quantity: number; note?: string }>
    ) => {
      if (newItems.length === 0) return;
      if (!canMutate) {
        toast.error('Bitte genau einen Debitor auswählen, um Artikel hinzuzufügen.');
        return;
      }
      try {
        const baseItems: MaterialRequestItemInput[] = currentDraft
          ? currentDraft.items.map((i) => ({
              articleNumber: i.articleNumber,
              quantity: toInputAnzahl(i),
              note: i.note ?? undefined,
            }))
          : [];

        const merged = [...baseItems];
        for (const item of newItems) {
          const idx = merged.findIndex(
            (m) => m.articleNumber === item.articleNumber
          );
          if (idx >= 0) {
            merged[idx].quantity += item.quantity;
          } else {
            merged.push(item);
          }
        }

        if (!currentDraft) {
          await createRequest({
            variables: { items: merged, ...adminMutationVars },
            refetchQueries: [...draftRefetch],
          });
        } else {
          await updateRequest({
            variables: {
              id: currentDraft.id,
              items: merged,
              ...adminMutationVars,
            },
            refetchQueries: [...draftRefetch],
          });
        }

        if (newItems.length === 1) {
          toast.success(
            `${newItems[0].articleNumber} zur Materialanfrage hinzugefügt`
          );
        } else {
          toast.success(
            `${newItems.length} Artikel zur Materialanfrage hinzugefügt`
          );
        }

        markHighlight('materialanfrage');

        if (!hasOpenedOnce.current) {
          setIsOpen(true);
          hasOpenedOnce.current = true;
        }
      } catch {
        toast.error('Fehler beim Hinzufügen. Bitte erneut versuchen.');
      }
    },
    [canMutate, currentDraft, createRequest, updateRequest, adminMutationVars, draftRefetch, markHighlight]
  );

  /**
   * "Erneut anfragen": übernimmt die Positionen einer (historischen) Anfrage
   * in den aktuellen DRAFT. Quelle ist eine bereits versendete Anfrage, deren
   * `quantity` die GESAMTMENGE ist — sie wird via getRequestAnzahl zurück in
   * die Eingabe-Anzahl konvertiert, sonst multipliziert der Resolver den
   * VE-Faktor ein zweites Mal.
   *
   * mode 'append'  → an bestehende Positionen anhängen (Duplikate per Menge addieren, via addItems)
   * mode 'replace' → bestehende Positionen vollständig ersetzen (update() im Resolver legt Items neu an)
   */
  const reorderFromRequest = useCallback(
    async (
      sourceItems: Array<
        Pick<MaterialRequestItem, 'articleNumber' | 'quantity' | 'quantityPerPackage' | 'piecesPerPackage' | 'note'>
      >,
      mode: 'append' | 'replace',
    ) => {
      const converted: MaterialRequestItemInput[] = sourceItems.map((i) => ({
        articleNumber: i.articleNumber,
        quantity: getRequestAnzahl(i),
        note: i.note ?? undefined,
      }));
      if (converted.length === 0) return;

      if (mode === 'append') {
        await addItems(converted);
        return;
      }

      // replace
      if (!canMutate) {
        toast.error('Bitte genau einen Debitor auswählen, um Artikel zu übernehmen.');
        return;
      }
      try {
        if (currentDraft) {
          await updateRequest({
            variables: { id: currentDraft.id, items: converted, ...adminMutationVars },
            refetchQueries: [...draftRefetch],
          });
        } else {
          await createRequest({
            variables: { items: converted, ...adminMutationVars },
            refetchQueries: [...draftRefetch],
          });
        }
        markHighlight('materialanfrage');
        toast.success('Anfrage übernommen');
      } catch {
        toast.error('Fehler beim Übernehmen der Anfrage. Bitte erneut versuchen.');
      }
    },
    [addItems, canMutate, currentDraft, updateRequest, createRequest, adminMutationVars, draftRefetch, markHighlight],
  );

  const removeItem = useCallback(
    async (articleNumber: string) => {
      if (!currentDraft) return;
      try {
        const updatedItems: MaterialRequestItemInput[] = currentDraft.items
          .filter((i) => i.articleNumber !== articleNumber)
          .map((i) => ({
            articleNumber: i.articleNumber,
            quantity: toInputAnzahl(i),
            note: i.note ?? undefined,
          }));

        if (updatedItems.length === 0) {
          await deleteRequest({
            variables: { id: currentDraft.id, ...adminMutationVars },
            refetchQueries: [...draftRefetch],
          });
        } else {
          await updateRequest({
            variables: { id: currentDraft.id, items: updatedItems, ...adminMutationVars },
            refetchQueries: [...draftRefetch],
          });
        }
      } catch {
        toast.error('Fehler beim Entfernen. Bitte erneut versuchen.');
      }
    },
    [currentDraft, updateRequest, deleteRequest, adminMutationVars, draftRefetch]
  );

  const updateQuantity = useCallback(
    async (articleNumber: string, quantity: number) => {
      if (!currentDraft) return;
      if (quantity <= 0) {
        await removeItem(articleNumber);
        return;
      }
      try {
        const updatedItems: MaterialRequestItemInput[] = currentDraft.items.map(
          (i) => ({
            articleNumber: i.articleNumber,
            quantity: i.articleNumber === articleNumber ? quantity : toInputAnzahl(i),
            note: i.note ?? undefined,
          })
        );
        await updateRequest({
          variables: { id: currentDraft.id, items: updatedItems, ...adminMutationVars },
          refetchQueries: [...draftRefetch],
        });
      } catch {
        toast.error('Fehler beim Aktualisieren. Bitte erneut versuchen.');
      }
    },
    [currentDraft, updateRequest, removeItem, adminMutationVars, draftRefetch]
  );

  const updateItemNote = useCallback(
    async (articleNumber: string, note: string) => {
      if (!currentDraft) return;
      try {
        const updatedItems: MaterialRequestItemInput[] = currentDraft.items.map(
          (i) => ({
            articleNumber: i.articleNumber,
            quantity: toInputAnzahl(i),
            note: i.articleNumber === articleNumber ? (note || undefined) : (i.note ?? undefined),
          })
        );
        await updateRequest({
          variables: { id: currentDraft.id, items: updatedItems, ...adminMutationVars },
          refetchQueries: [...draftRefetch],
        });
      } catch {
        toast.error('Fehler beim Speichern der Notiz.');
      }
    },
    [currentDraft, updateRequest, adminMutationVars, draftRefetch]
  );

  const submit = useCallback(
    async (
      commission?: string,
      desiredDeliveryDate?: string,
      desiredDeliveryWeek?: string,
      note?: string
    ) => {
      if (!currentDraft) return;
      try {
        await updateRequest({
          variables: {
            id: currentDraft.id,
            commission,
            desiredDeliveryDate: desiredDeliveryDate ?? null,
            desiredDeliveryWeek: desiredDeliveryWeek ?? null,
            note,
            ...adminMutationVars,
          },
        });
        const { data } = await submitRequest({
          variables: { id: currentDraft.id, ...adminMutationVars },
          refetchQueries: [...draftRefetch],
        });
        const submitted = data?.submitMaterialRequest;
        if (submitted?.navDocumentNo) {
          setJustSubmittedNav({
            navDocumentNo: submitted.navDocumentNo,
            navImportedAt: submitted.navImportedAt ?? null,
          });
        }
        setIsOpen(false);
        hasOpenedOnce.current = false;
        toast.success('Anfrage erfolgreich gesendet!');
      } catch {
        toast.error('Fehler beim Senden der Anfrage. Bitte erneut versuchen.');
      }
    },
    [currentDraft, updateRequest, submitRequest, adminMutationVars, draftRefetch]
  );

  const clear = useCallback(async () => {
    if (!currentDraft) return;
    try {
      await deleteRequest({
        variables: { id: currentDraft.id, ...adminMutationVars },
        refetchQueries: [...draftRefetch],
      });
      toast.success('Materialanfrage geleert');
    } catch {
      toast.error('Fehler beim Leeren der Liste.');
    }
  }, [currentDraft, deleteRequest, adminMutationVars, draftRefetch]);

  /**
   * Persistiert die Kopf-Felder (Kommission, Wunschtermin, Nachricht) auf dem
   * DRAFT. Bewusst OHNE `items` (Positionen bleiben unangetastet) und OHNE
   * `refetchQueries` — die Mutation liefert das aktualisierte Objekt zurück,
   * Apollo aktualisiert den Cache per `id`.
   */
  const updateHeader = useCallback(
    async (fields: {
      commission?: string;
      desiredDeliveryDate?: string | null;
      desiredDeliveryWeek?: string | null;
      note?: string;
    }) => {
      if (!currentDraft) return;
      try {
        await updateRequest({
          variables: { id: currentDraft.id, ...fields, ...adminMutationVars },
        });
      } catch {
        toast.error('Fehler beim Speichern der Anfrage-Angaben.');
      }
    },
    [currentDraft, updateRequest, adminMutationVars],
  );

  return {
    currentDraft,
    justSubmittedNav,
    dismissNavBanner,
    items,
    itemCount,
    netTotal,
    loading,
    refetch,
    isOpen,
    toggleCart: useCallback(() => setIsOpen((prev) => !prev), []),
    openCart: useCallback(() => setIsOpen(true), []),
    closeCart: useCallback(() => setIsOpen(false), []),
    addItem,
    addItems,
    reorderFromRequest,
    removeItem,
    updateQuantity,
    updateItemNote,
    submit,
    clear,
    updateHeader,
    findExistingItem,
    canMutate,
  };
}
